import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router";
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import { callAlertWeb, callNativeWeb, footerShowEventWeb } from 'common/appInterface';
import { useLoading } from 'components/Loading/LoadingProvider';

const DeliveryInputSearchPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const screenId = "APPL13";
  const screenName = t("APPL13.title");

  const [cardApply] = useState<Partial<CardApplyStep6>>(location.state.apply);
  const [nextPage, setNextPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [jusoList, setJusoList] = useState<Juso[]>();
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 주소검색 */
  const callJusoFirstSearch = useCallback(async () => {
    showLoading();
    await client
      .get('/api/app/apply/juso/search', {
        params: { ...location.state.query, currentPage: '1', keyword: keyword }
      })
      .then((response) => {
        let result = response.data.response;
        console.log(result);
        setNextPage(Number.parseInt(result.currentPage) + 1);
        setHasMore(
          Number.parseInt(result.results.length) ===
            Number.parseInt(result.countPerPage)
        );
        setJusoList(result.results);
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, keyword, showLoading, hideLoading]);

  /** 주소검색 Next */
  const callJusoNextSearch = useCallback(async () => {
    showLoading();
    await client
      .get('/api/app/apply/juso/search', {
        params: {
          ...location.state.query,
          currentPage: nextPage,
          keyword: keyword
        }
      })
      .then((response) => {
        let result = response.data.response;
        console.log(result);
        setNextPage(Number.parseInt(result.currentPage) + 1);
        setHasMore(
          Number.parseInt(result.results.length) ===
            Number.parseInt(result.countPerPage)
        );
        setJusoList([...jusoList!, ...result.results]);
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, nextPage, keyword, jusoList, showLoading, hideLoading]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    // 포커스 설정
    setTimeout(() => inputRef.current?.focus(), 500);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents donationBasket ">
          <div className="addressWrap">
            <div className="searchBox">
              <div className="inputBoxTy03 inputBoxTySearch">
                <input type="text" className="inpSearchForm ui-autocomplete-input" placeholder={t("APPL13.explan01")} autoComplete="off" value={keyword} onChange={(e) => setKeyword(e.target.value)} ref={inputRef} onKeyDown={(event) => { if (event.key === "Enter") callJusoFirstSearch(); }} />
                <span className="line"></span>
                <button type="button" className="icoInputDelete btnDelete" style={{ display: stringUtils.checkValueNull(keyword) ? "none" : "block" }} onClick={() => setKeyword("")}><span className="blind">삭제</span></button>
                <button type="button" className="icoSearch btnSearch" onClick={() => callJusoFirstSearch()}><span className="blind">검색</span></button>
              </div>
              <p className="message">{t("APPL13.explan02")} {t("APPL13.explan03")}</p>
            </div>
          </div>
          <div className="inner">
            <ul className="addressList">
              {jusoList &&
                <InfiniteScroll dataLength={jusoList.length} next={callJusoNextSearch} hasMore={hasMore} loader={""}>
                  {jusoList.length > 0 && jusoList.map((value) =>
                    <Link to="/apply/step6/input" state={{ ...location.state, apply: { ...cardApply, homeZipCode: value.zipNo, homeEngAddress: value.roadAddr, homeNewAddress: value.korAddr, homeNewDetailAddress: "", homeRoadManagementNo: value.rnMgtSn }}} className="goToPage" key={value.roadAddr}>
                      <li className="item">
                        <div className="address">
                          <dl>
                            <dt>도로명</dt>
                            <dd>{value.roadAddr}</dd>
                          </dl>
                          <dl>
                            <dt>지번</dt>
                            <dd>{value.jibunAddr}</dd>
                          </dl>
                        </div>
                        <div className="add_num">
                          <p>{value.zipNo}</p>
                        </div>
                      </li>
                    </Link>
                  )}
                </InfiniteScroll>
              }
              {jusoList && jusoList.length === 0 &&
                <li className="item">
                  <div className="address">
                    <dl>
                      <dd>{t("APPL13.nodata")}</dd>
                    </dl>
                  </div>
                </li>
              }
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DeliveryInputSearchPage;